// Commitment conflict detection for Trade Offers. Cards are deliberately not
// reserved while an offer is pending, so the same copy can be promised twice:
// once to a pending offer and once to a new offer or an async send. This check
// fires at commitment time on both surfaces (offer builder + Send Cards flow).
//
// Predicate, per ruling: a conflict exists only on a REAL shortfall —
// committed-in-pending-offers + this new commitment > currently owned quantity.
// Firing when spare copies exist would train users to dismiss the warning.

import {
  CommittedQuantityEntry,
  MyCommittedQuantitiesResponse,
} from 'types/interfaces/tradeOffer';

export interface CommitmentConflict {
  cardName: string;
  committedQuantity: number;
  requestedQuantity: number;
  ownedQuantity: number;
  /** Pending offers holding the committed copies (for the cancel-that-offer option) */
  offerIds: string[];
  /** Present when the conflict was joined by have row (builder surface) */
  userMTGCardHaveId?: number;
}

export interface HaveIdCommitmentInput {
  userMTGCardHaveId: number;
  quantity: number;
  ownedQuantity: number;
  cardName: string;
}

export interface CardKeyCommitmentInput {
  cardId: number;
  condition: string;
  language: string;
  foil: boolean;
  quantity: number;
  ownedQuantity: number;
  cardName: string;
}

/** Key space of my-committed-quantities byCardKey: `${cardId}|${CONDITION}|${lang}|foil-or-nonfoil` */
export const buildCardKey = (
  cardId: number,
  condition: string,
  language: string,
  foil: boolean
): string => `${cardId}|${condition}|${language}|${foil ? 'foil' : 'nonfoil'}`;

const toConflict = (
  input: { cardName: string; quantity: number; ownedQuantity: number },
  entry: CommittedQuantityEntry,
  userMTGCardHaveId?: number
): CommitmentConflict | null => {
  if (entry.committedQuantity + input.quantity <= input.ownedQuantity) return null;
  return {
    cardName: input.cardName,
    committedQuantity: entry.committedQuantity,
    requestedQuantity: input.quantity,
    ownedQuantity: input.ownedQuantity,
    offerIds: entry.offerIds,
    userMTGCardHaveId,
  };
};

/** Builder surface: line items are have-row-shaped */
export const findHaveIdConflicts = (
  items: HaveIdCommitmentInput[],
  byHaveId: MyCommittedQuantitiesResponse['byHaveId']
): CommitmentConflict[] => {
  const conflicts: CommitmentConflict[] = [];
  for (const item of items) {
    const entry = byHaveId[String(item.userMTGCardHaveId)];
    if (!entry) continue;
    const conflict = toConflict(item, entry, item.userMTGCardHaveId);
    if (conflict) conflicts.push(conflict);
  }
  return conflicts;
};

export interface CardIdCommitmentInput {
  cardId: number;
  quantity: number;
  cardName: string;
}

/**
 * Send Cards surface. Owned quantities are only available per printing
 * (quantity-summary's quantitiesByCardId), so the check aggregates committed
 * copies across all conditions/languages/finishes of the printing and compares
 * against total owned. Owned-per-printing is always >= owned-per-composite, so
 * this can never fire without a true shortfall — at the cost of missing some
 * cross-condition conflicts, which the atomic accept re-validation backstops.
 */
export const findCardIdConflicts = (
  items: CardIdCommitmentInput[],
  byCardKey: MyCommittedQuantitiesResponse['byCardKey'],
  ownedByCardId: Record<string, number>
): CommitmentConflict[] => {
  const committedByCardId = new Map<number, { committedQuantity: number; offerIds: Set<string> }>();
  for (const [key, entry] of Object.entries(byCardKey)) {
    const cardId = Number(key.split('|')[0]);
    if (!Number.isFinite(cardId)) continue;
    const agg = committedByCardId.get(cardId) || { committedQuantity: 0, offerIds: new Set() };
    agg.committedQuantity += entry.committedQuantity;
    entry.offerIds.forEach((id) => agg.offerIds.add(id));
    committedByCardId.set(cardId, agg);
  }

  const requestedByCardId = new Map<number, { quantity: number; cardName: string }>();
  for (const item of items) {
    const existing = requestedByCardId.get(item.cardId);
    if (existing) {
      existing.quantity += item.quantity;
    } else {
      requestedByCardId.set(item.cardId, { quantity: item.quantity, cardName: item.cardName });
    }
  }

  const conflicts: CommitmentConflict[] = [];
  requestedByCardId.forEach((requested, cardId) => {
    const committed = committedByCardId.get(cardId);
    if (!committed) return;
    const owned = ownedByCardId[String(cardId)] ?? 0;
    if (committed.committedQuantity + requested.quantity <= owned) return;
    conflicts.push({
      cardName: requested.cardName,
      committedQuantity: committed.committedQuantity,
      requestedQuantity: requested.quantity,
      ownedQuantity: owned,
      offerIds: Array.from(committed.offerIds),
    });
  });
  return conflicts;
};

/** Attribute-shaped rows joined on the full composite key (used where exact owned-per-composite is known) */
export const findCardKeyConflicts = (
  items: CardKeyCommitmentInput[],
  byCardKey: MyCommittedQuantitiesResponse['byCardKey']
): CommitmentConflict[] => {
  const conflicts: CommitmentConflict[] = [];
  for (const item of items) {
    const entry = byCardKey[buildCardKey(item.cardId, item.condition, item.language, item.foil)];
    if (!entry) continue;
    const conflict = toConflict(item, entry);
    if (conflict) conflicts.push(conflict);
  }
  return conflicts;
};
